import { useState } from "react";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PERMISSIONS, STAFF } from "@/data/demo";
import { Avatar, Btn, Card, PageHeader } from "@/components/common";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const demo = () => toast("Demo mode — this action is visual only.");

const PermissionsModal = ({ member, onClose }) => {
  const [perms, setPerms] = useState(() => Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.on])));
  const save = () => {
    onClose();
    toast.success("Permissions updated", { description: "Demo mode — nothing was saved." });
  };
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-permissions-modal" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Modify permissions</DialogTitle>
          <DialogDescription>Control what {member?.name} can do in your practice.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col divide-y divide-slate-100">
          {PERMISSIONS.map((p) => (
            <label key={p.key} className="flex min-h-[52px] cursor-pointer items-center justify-between gap-4">
              <span className="text-sm font-medium text-slate-700">{p.label}</span>
              <Switch data-testid={`staff-permission-toggle-${p.key}`} checked={perms[p.key]} onCheckedChange={(v) => setPerms((s) => ({ ...s, [p.key]: v }))} className="data-[state=checked]:bg-gold" />
            </label>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Btn variant="outline" data-testid="permissions-cancel" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" data-testid="permissions-save" onClick={save}>Save permissions</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Staff() {
  const [member, setMember] = useState(null);
  return (
    <div>
      <PageHeader title="Staff" subtitle="Create, remove and control access for your team">
        <Btn data-testid="create-staff-button" variant="gold" onClick={demo}><Plus size={16} />Create Staff</Btn>
      </PageHeader>
      <div data-testid="staff-list" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {STAFF.map((s) => (
          <Card key={s.id} data-testid={`staff-card-${s.id}`} className="p-5">
            <div className="flex items-center gap-4">
              <Avatar name={s.name} />
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-navy">{s.name}</div>
                <div className="text-sm text-slate-500">{s.role}</div>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${s.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.status}</span>
            </div>
            <Btn variant="primary" className="mt-4 w-full" data-testid={`modify-permissions-${s.id}`} onClick={() => setMember(s)}><KeyRound size={15} />Modify Permissions</Btn>
            <div className="mt-2 flex gap-2">
              <Btn variant="outline" className="flex-1" data-testid={`staff-edit-${s.id}`} onClick={demo}><Pencil size={15} />Edit</Btn>
              <Btn variant="danger" className="flex-1" data-testid={`staff-delete-${s.id}`} onClick={demo}><Trash2 size={15} />Delete</Btn>
            </div>
          </Card>
        ))}
      </div>
      <PermissionsModal key={member?.id} member={member} onClose={() => setMember(null)} />
    </div>
  );
}
